import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Fade } from 'react-awesome-reveal';
import { Logo } from '../../components/Logo';
import { Button } from '../../components/Button';
import { ProgressiveImage } from '../../components/ProgressiveImage';
import { CardSelectorStage } from '../../components/CardSelectorStage';
import { RoleSelectorStage } from '../../components/RoleSelectorStage';
import { StarCardSelectorStage } from '../../components/StarCardSelectorStage';
import { TeamPowerIndicator } from '../../components/TeamPowerIndicator';
import { CardSelectorStageMobile } from '../../components/CardSelectorStageMobile';
import { MarketplaceStage } from '../../components/MarketplaceStage';
import { PremiumInformationStage } from '../../components/PremiumInformationStage';
import { SeasonRewardsInformationStage } from '../../components/SeasonRewardsInformationStage';
import { logEvent } from '../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../lib/shared/event-types';
import { preloadImages } from '../../lib/shared/preload-images';
import cardImage1Src from '../../assets/images/prelander/v6/ava_02.png';
import cardImage2Src from '../../assets/images/prelander/v6/darryl_01.png';
import cardImage3Src from '../../assets/images/prelander/v6/rezan_03.png';
import cardImage4Src from '../../assets/images/prelander/v6/sally_01.png';
import cardImage5Src from '../../assets/images/prelander/v6/ricksaw_03.png';
import cardImage6Src from '../../assets/images/prelander/v6/ava_03.png';
import mainImageSrc from '../../assets/images/prelander/v6/main-v6.png';
import bundleTrainingKitImageSrc from '../../assets/images/prelander/v6/bundle-training-kit.png';
import seasonTournamentRewardsImageSrc from '../../assets/images/prelander/v6/tour-rewards.png';
import rewardsImageSrc from '../../assets/images/prelander/v6/rewards-new.png';
import goldPrizeImageSrc from '../../assets/images/prelander/v6/skin-1st.png';
import silverPrizeImageSrc from '../../assets/images/prelander/v6/skin-2nd.png';
import bronzePrizeImageSrc from '../../assets/images/prelander/v6/skin-3rd.png';
import { useExternalLink } from '../../shared/useExternalLink';
import styles from './PrelanderV6Page.css';

const cx = classnames.bind(styles);

const OPEN_BUNDLE_PREVIEW_STAGE = 'openBundlePreviewStage';
const CARD_SELECTOR_STAGE = 'cardSelectorStage';
const MARKETPLACE_STAGE = 'marketplaceStage';
const ROLE_SELECTOR_STAGE = 'roleSelectorStage';
const PREMIUM_INFORMATION_STAGE = 'premiumInformationStage';
const STAR_CARD_SELECTOR_STAGE = 'starCardSelectorStage';
const RESULTS_STAGE = 'resultsStage';
const SEASON_REWARDS_INFORMATION_STAGE = 'seasonRewardsInformationStage';

const STAGES = [
    OPEN_BUNDLE_PREVIEW_STAGE,
    CARD_SELECTOR_STAGE,
    MARKETPLACE_STAGE,
    ROLE_SELECTOR_STAGE,
    PREMIUM_INFORMATION_STAGE,
    STAR_CARD_SELECTOR_STAGE,
    RESULTS_STAGE,
    SEASON_REWARDS_INFORMATION_STAGE
];

const LEADER_ROLE = 'leader';
const SHOOTER_ROLE = 'shooter';
const DEFENDER_ROLE = 'defender';

const CARDS = [
    {
        imageUrl: cardImage1Src,
        id: 1,
        role: LEADER_ROLE,
        selectedRole: '',
        isStarCard: false,
        level: 2,
        name: 'AVA'
    },
    {
        imageUrl: cardImage2Src,
        id: 2,
        role: SHOOTER_ROLE,
        selectedRole: '',
        isStarCard: false,
        level: 1,
        name: 'DARRYL'
    },
    {
        imageUrl: cardImage3Src,
        id: 3,
        role: DEFENDER_ROLE,
        selectedRole: '',
        isStarCard: false,
        level: 3,
        name: 'REZAN'
    },
    {
        imageUrl: cardImage4Src,
        id: 4,
        role: DEFENDER_ROLE,
        selectedRole: '',
        isStarCard: false,
        level: 1,
        name: 'SALLY'
    },
    {
        imageUrl: cardImage5Src,
        id: 5,
        role: DEFENDER_ROLE,
        selectedRole: '',
        isStarCard: false,
        level: 3,
        name: 'RICKSAW'
    }
];

const AVA_CARD_ID = 6;

const OFFER_CARD = {
    imageUrl: cardImage6Src,
    id: AVA_CARD_ID,
    role: LEADER_ROLE,
    selectedRole: '',
    isStarCard: false,
    level: 3,
    name: 'AVA'
};

const MAX_SELECTED_CARDS = 3;
const SLIDE_3 = 3;
const SLIDE_6 = 6;

const INITIAL_PRELANDER = 'v6';

const IMAGES = [
    bundleTrainingKitImageSrc,
    ...CARDS.map(card => card.imageUrl),
    rewardsImageSrc,
    cardImage6Src,
    seasonTournamentRewardsImageSrc,
    goldPrizeImageSrc,
    silverPrizeImageSrc,
    bronzePrizeImageSrc
];

function PrelanderV6Page({subscriptionPathname = 'subscription/v4', initialPrelander = INITIAL_PRELANDER, subscriptionQuery = ''}) {
    const [isPreview, setIsPreview] = useState(true);
    const [selectedCards, setSelectedCards] = useState([]);
    const [cardIdByOffer, setCardIdByOffer] = useState(0);
    const [stage, setStage] = useState('');
    const stageIndex = STAGES.indexOf(stage) + 1;

    useEffect(() => {
        logEvent(EVENT_TYPES.USER_PROPERTIES, {initialPrelander});
        logEvent(EVENT_TYPES.V6_PRELANDER_VISITED);

        if (isPreview) {
            preloadImages(IMAGES);
        }
    }, []);

    useEffect(() => {
        if (stageIndex === SLIDE_3) {
            logEvent(EVENT_TYPES.V6_SLIDE_3_SCREEN_VISITED);
        }

        if (stageIndex === SLIDE_6) {
            logEvent(EVENT_TYPES.V6_SLIDE_6_SCREEN_VISITED);
        }
    }, [stageIndex]);

    const selectCard = card => {
        setSelectedCards(cards => {
            if (cards.some(selectedCard => selectedCard.id === card.id)) {
                return cards.filter(selectedCard => selectedCard.id !== card.id);
            }

            if (cards.length === MAX_SELECTED_CARDS) {
                return cards;
            }

            return [...cards, card];
        });
    };

    const replaceCardAndNextRoleSelectorStage = (id, newCard) => {
        setSelectedCards(cards => cards.map(card => card.id === id ? newCard : card));
        setStage(ROLE_SELECTOR_STAGE);
    };

    const selectRoleByCard = (role, cardId) => {
        setSelectedCards(cards => cards.map(card => card.id === cardId ? {...card, selectedRole: role} : card));
    };

    const selectStarCard = cardId => {
        setSelectedCards(cards => cards.map(card => ({...card, isStarCard: card.id === cardId})));
    };

    const startGame = () => {
        setIsPreview(false);
        setStage(OPEN_BUNDLE_PREVIEW_STAGE);
    };

    const nextCardSelectorStage = () => {
        setStage(CARD_SELECTOR_STAGE);
    };

    const nextMarketplaceStage = () => {
        setStage(MARKETPLACE_STAGE);
    };

    const nextStarCardSelectorStage = () => {
        setStage(STAR_CARD_SELECTOR_STAGE);
    };

    const nextPremiumInformationStage = () => {
        setStage(PREMIUM_INFORMATION_STAGE);
    };

    const nextResultsStage = () => {
        setStage(RESULTS_STAGE);
    };

    const nextSeasonRewardsStage = () => {
        setStage(SEASON_REWARDS_INFORMATION_STAGE);
    };

    const getResults = () => {
        const avaCard = selectedCards.find(selectedCard => selectedCard.id === AVA_CARD_ID && selectedCard.isStarCard);

        if (avaCard) {
            return {
                teamPowerValue: 100
            }
        }

        const isStarCard = selectedCards.some(selectedCard => selectedCard.isStarCard);

        if (!isStarCard) {
            return {
                teamPowerValue: 75
            }
        }

        return {
            teamPowerValue: 90
        }
    };

    const renderContent = () => {
        if (isPreview) {
            return (
                <>
                    <main className={styles.main}>
                        <span className={styles.label}>For new users only</span>
                        <h1 className={styles.title}>
                            Choose the best game roles according to the skills of the cards and get your prize...
                        </h1>
                        <ProgressiveImage
                            alt='counter-strike team'
                            src={mainImageSrc}
                            className={styles.image}
                        />
                        <p className={styles.description}>...50 game cards ultimate subscription box</p>
                    </main>
                    <Button
                        label='Continue'
                        className={styles.button}
                        onClick={startGame}
                        type='sixth'
                    />
                </>
            )
        };

        if (stage === OPEN_BUNDLE_PREVIEW_STAGE) {
            return (
                <Fade
                    triggerOnce
                    duration={300}
                >
                    <main className={styles.main}>
                        <span className={styles.label}>The first task is simple</span>
                        <h1 className={styles.title}>
                            Open your first bundle to get started
                        </h1>
                        <ProgressiveImage
                            alt='bundle training kit'
                            src={`${bundleTrainingKitImageSrc}?v=2`}
                            className={styles.image}
                        />
                    </main>
                    <Button
                        label='Open to continue'
                        className={styles.button}
                        onClick={nextCardSelectorStage}
                        type='sixth'
                    />
                </Fade>
            )
        };

        if (stage === CARD_SELECTOR_STAGE) {
            const numberOfCardsLeft = MAX_SELECTED_CARDS - selectedCards.length;
            const teamPowerValue = selectedCards.length / MAX_SELECTED_CARDS * 25;

            return (
                <>
                    <main className={cx('main', 'mainWithLarge', 'mainWithIndent')}>
                        <h1 className={cx('secondaryTitle', 'secondaryTitleWithoutIndentOnMobile')}>
                            Pick the {MAX_SELECTED_CARDS} best cards out of {CARDS.length}
                        </h1>
                        <CardSelectorStage
                            cards={CARDS}
                            selectedCards={selectedCards}
                            selectCard={selectCard}
                            disabled={selectedCards.length === MAX_SELECTED_CARDS}
                        />
                    </main>
                    <p className={cx('cardsLeftText', 'hiddenOnMobile')}>{numberOfCardsLeft} card{numberOfCardsLeft > 1 ? 's' : ''} left</p>
                    <TeamPowerIndicator value={teamPowerValue} className={styles.hiddenOnMobile} />
                    <CardSelectorStageMobile
                        cards={CARDS}
                        selectedCards={selectedCards}
                        selectCard={selectCard}
                        finishStage={nextMarketplaceStage}
                        numberOfCards={MAX_SELECTED_CARDS}
                    >
                        {(isSkippedCard) => (
                            <TeamPowerIndicator value={teamPowerValue} withInitAnimate={!isSkippedCard} />
                        )}
                    </CardSelectorStageMobile>
                    <Button
                        label='Continue'
                        className={cx('button', 'hiddenOnMobile')}
                        disabled={selectedCards.length !== MAX_SELECTED_CARDS}
                        onClick={nextMarketplaceStage}
                        type='sixth'
                    />
                </>
            )
        };

        if (stage === MARKETPLACE_STAGE) {
            return (
                <Fade
                    triggerOnce
                    duration={300}
                >
                    <main className={cx('main', 'mainWithBig', 'mainWithIndent')}>
                        <h1 className={styles.secondaryTitle}>
                            You have a premium offer from the marketplace. <br />
                            Choose the card you want to replace
                        </h1>
                        <MarketplaceStage
                            cards={selectedCards}
                            selectedCardId={cardIdByOffer}
                            setCardId={setCardIdByOffer}
                            offerCard={OFFER_CARD}
                        />
                    </main>
                    <TeamPowerIndicator value={25 + (cardIdByOffer ? 25 : 0)} />
                    <Button
                        label='Continue'
                        className={styles.button}
                        disabled={!cardIdByOffer}
                        onClick={() => replaceCardAndNextRoleSelectorStage(cardIdByOffer, OFFER_CARD)}
                        type='sixth'
                    />
                </Fade>
            )
        };

        if (stage === ROLE_SELECTOR_STAGE) {
            const numberOfSelectedCardsWithRole = selectedCards.filter(card => Boolean(card.selectedRole)).length;
            const teamPowerValue = 50 + (numberOfSelectedCardsWithRole / MAX_SELECTED_CARDS * 25);

            return (
                <>
                    <main className={cx('main', 'mainWithIndent')}>
                        <h1 className={styles.secondaryTitle}>
                            Which play role is best suited for each card?
                        </h1>
                        <RoleSelectorStage
                            cards={selectedCards}
                            selectRoleByCard={selectRoleByCard}
                            finishStage={nextPremiumInformationStage}
                            variant='secondary'
                        />
                    </main>
                    <TeamPowerIndicator value={teamPowerValue} />
                    <Button
                        label='Continue'
                        className={cx('button', 'hiddenOnMobile')}
                        disabled={selectedCards.some(card => !card.selectedRole)}
                        onClick={nextPremiumInformationStage}
                        type='sixth'
                    />
                </>
            )
        };

        if (stage === PREMIUM_INFORMATION_STAGE) {
            return (
                <Fade
                    triggerOnce
                    duration={300}
                >
                    <main className={cx('main', 'mainWithIndent')}>
                        <span className={styles.label}>Pay particular attention</span>
                        <h1 className={styles.title}>
                            The difference between regular and premium powerplay users
                        </h1>
                        <PremiumInformationStage />
                    </main>
                    <Button
                        label='Continue'
                        className={styles.button}
                        onClick={nextStarCardSelectorStage}
                        type='sixth'
                    />
                </Fade>
            )
        };

        if (stage === STAR_CARD_SELECTOR_STAGE) {
            const {teamPowerValue} = getResults();

            return (
                <>
                    <main className={cx('main', 'mainWithIndent')}>
                        <h1 className={styles.secondaryTitle}>
                            Pick the “star” player who gets bonus points
                        </h1>
                        <StarCardSelectorStage
                            cards={selectedCards}
                            selectStarCard={selectStarCard}
                            className={styles.starCardSelectorStage}
                        />
                    </main>
                    <TeamPowerIndicator value={teamPowerValue} />
                    <Button
                        label='Continue'
                        className={styles.button}
                        onClick={nextResultsStage}
                        disabled={!selectedCards.some(card => card.isStarCard)}
                        type='sixth'
                    />
                </>
            )
        };

        if (stage === RESULTS_STAGE) {
            const {teamPowerValue} = getResults();

            return (
                <Fade
                    triggerOnce
                    duration={300}
                >
                    <main className={styles.main}>
                        <span className={styles.label}>congratulations!</span>
                        <h1 className={styles.title}>
                            Top reward unlocked
                        </h1>
                        <div className={styles.rewardBlock}>
                            <ProgressiveImage
                                alt='reward'
                                src={rewardsImageSrc}
                                className={styles.rewardImage}
                            />
                        </div>
                        <TeamPowerIndicator value={teamPowerValue} className={styles.teamPowerIndicator} />
                        <p className={styles.description}>50 game cards ultimate subscription box is already waiting for you on the platform.</p>
                    </main>
                    <Button
                        label='Claim all rewards'
                        className={styles.button}
                        onClick={nextSeasonRewardsStage}
                        type='sixth'
                    />
                </Fade>
            )
        };

        if (stage === SEASON_REWARDS_INFORMATION_STAGE) {
            const {url: subscriptionUrl, onClick: clickOnClaimRewards} = useExternalLink(subscriptionPathname, subscriptionQuery);

            return (
                <Fade
                    triggerOnce
                    duration={300}
                    key={SEASON_REWARDS_INFORMATION_STAGE}
                >
                    <main className={cx('main', 'mainWithIndent')}>
                        <span className={styles.label}>Congratulations to our winners!</span>
                        <h1 className={styles.title}>
                            Season 4 rewards
                        </h1>
                        <SeasonRewardsInformationStage />
                        <h2 className={styles.title}>
                            Season tournaments rewards
                        </h2>
                        <div className={styles.seasonTournamentRewardsImageWrapper}>
                            <ProgressiveImage
                                alt='Season tournament rewards'
                                src={seasonTournamentRewardsImageSrc}
                                className={styles.seasonTournamentRewardsImage}
                            />
                        </div>
                    </main>
                    <Button
                        label='Continue'
                        className={styles.button}
                        onClick={clickOnClaimRewards}
                        link={{
                            to: subscriptionUrl,
                            target: 'custom'
                        }}
                        type='sixth'
                    />
                </Fade>
            )
        };

        return null;
    };

    const {url: mainUrl, onClick: clickOnLogin} = useExternalLink('counter-strike', 'auth=true');

    return (
        <div className={styles.container}>
            <div className={styles.headerWrapper}>
                <header className={styles.header}>
                    <div className={styles.headerMainInformation}>
                        <Logo className={styles.logo} />
                        {!stageIndex && (
                            <p className={styles.headerTitle}>Free-to-play card fantasy game for counter-strike</p>
                        )}
                    </div>
                    {Boolean(stageIndex)
                        ? <p className={styles.progressText}>{stageIndex}/{STAGES.length}</p>
                        : (
                            <Button
                                iconLeft={{type: 'login'}}
                                link={{
                                    to: mainUrl
                                }}
                                onClick={clickOnLogin}
                                type='tertiary'
                            />
                        )
                    }
                </header>
                <div className={styles.progressIndicator}>
                    <div
                        className={styles.progressActiveBar}
                        style={{width: `${stageIndex / STAGES.length * 100}%`}}
                    />
                </div>
            </div>
           {renderContent()}
            <footer className={styles.footer}>
                <p className={styles.copyrightText}>
                    Powerplay is developed independently and is not affiliated with or endorsed by the players, video game developers, esports teams or event holders depicted in the game.
                </p>
            </footer>
        </div>
    )
}

export {
    PrelanderV6Page
}
